import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import authRoutes from '@navigation/authRoutes';
import Auth from '@layouts/Auth';
import System from '@layouts/System';
import Loader from '@components/Utilities/Loader';
import { getRoutesByProfile } from '@utils/Routes';
import { useAuthBootstrap } from '@hooks/useAuthBootstrap';

interface RouteProps {
    children: React.ReactElement;
}

const ProtectedRoute: React.FunctionComponent<RouteProps> = (props) => {
    const { profileSelected } = useSelector((state: RootReducer) => state.auth);
    if (!profileSelected) {
        return <Navigate to="/" replace />;
    }
    return props.children;
};

const UnauthenticatedRoute: React.FunctionComponent<RouteProps> = (props) => {
    const { profileSelected } = useSelector((state: RootReducer) => state.auth);
    if (profileSelected) {
        return <Navigate to={`/${profileSelected}/home`} replace />;
    }
    return props.children;
};

const AppRouter: React.FunctionComponent = () => {
    const { profileSelected } = useSelector((state: RootReducer) => state.auth);
    const { authChecked } = useAuthBootstrap();

    // While Firebase resolves the initial auth state we don't yet know whether
    // there is a session, so we hold rendering to avoid flashing the wrong screen.
    if (!authChecked) {
        return <Loader />;
    }

    return (
        <Routes>
            <Route
                path="/"
                element={
                    <UnauthenticatedRoute>
                        <Auth />
                    </UnauthenticatedRoute>
                }
            >
                {authRoutes.map((prop, key) => {
                    return <Route path={prop.path} element={<prop.element />} key={key} />;
                })}
            </Route>
            {profileSelected && (
                <Route
                    path={`/${profileSelected}`}
                    element={
                        <ProtectedRoute>
                            <System />
                        </ProtectedRoute>
                    }
                >
                    {/* Entering "/{profile}" exactly redirects to its home so the layout
                        is never rendered with an empty Outlet (blank screen). */}
                    <Route index element={<Navigate to="home" replace />} />
                    {getRoutesByProfile(profileSelected).map((prop, key) => {
                        return <Route path={prop.path} element={<prop.element />} key={key} />;
                    })}
                </Route>
            )}
            <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    );
};

export default AppRouter;
